export type HeightInFeet = {
  feet: number;
  inches: number;
};

export const HEIGHT_GLOBAL_MIN_CM = 127;
export const HEIGHT_GLOBAL_MAX_CM = 220;
export const HEIGHT_SLIDER_WINDOW_CM = 30;

export function clampHeightCm(value: number): number {
  return Math.max(HEIGHT_GLOBAL_MIN_CM, Math.min(HEIGHT_GLOBAL_MAX_CM, Math.round(value)));
}

export function getHeightSliderBounds(
  userHeight: number | undefined,
  selectionMin: number,
  selectionMax: number,
): { tightMin: number; tightMax: number; renderMin: number; renderMax: number } {
  if (!userHeight) {
    return {
      tightMin: HEIGHT_GLOBAL_MIN_CM,
      tightMax: HEIGHT_GLOBAL_MAX_CM,
      renderMin: HEIGHT_GLOBAL_MIN_CM,
      renderMax: HEIGHT_GLOBAL_MAX_CM,
    };
  }

  const tightMin = Math.max(HEIGHT_GLOBAL_MIN_CM, userHeight - HEIGHT_SLIDER_WINDOW_CM);
  const tightMax = Math.min(HEIGHT_GLOBAL_MAX_CM, userHeight + HEIGHT_SLIDER_WINDOW_CM);
  return {
    tightMin,
    tightMax,
    renderMin: Math.min(tightMin, selectionMin),
    renderMax: Math.max(tightMax, selectionMax),
  };
}

export function cmToFeet(cm: number): HeightInFeet {
  const totalInches = cm / 2.54;
  let feet = Math.floor(totalInches / 12);
  let inches = Math.round(totalInches % 12);
  if (inches === 12) {
    feet += 1;
    inches = 0;
  }
  return { feet, inches };
}

export function feetToCm(feet: number, inches: number): number {
  return Math.round((feet * 12 + inches) * 2.54);
}

export function shouldUseImperial(): boolean {
  if (typeof navigator === "undefined") return false;
  const locale = navigator.language || "en-US";
  return locale.startsWith("en-US") || locale.startsWith("en-LR") || locale.startsWith("my-MM");
}

export const HEIGHT_DISPLAY_UNIT_KEY = "yara-height-display-imperial";

export function readHeightDisplayImperial(): boolean {
  if (typeof window === "undefined") return shouldUseImperial();
  const stored = window.localStorage.getItem(HEIGHT_DISPLAY_UNIT_KEY);
  if (stored === "true") return true;
  if (stored === "false") return false;
  return shouldUseImperial();
}

export function writeHeightDisplayImperial(useImperial: boolean) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(HEIGHT_DISPLAY_UNIT_KEY, String(useImperial));
}

export function formatHeight(heightInCm: number, useImperial: boolean): string {
  if (useImperial) {
    const { feet, inches } = cmToFeet(heightInCm);
    return `${feet}'${inches}"`;
  }
  return `${heightInCm} cm`;
}

function formatHeightDiff(diffInCm: number, useImperial: boolean): string {
  if (useImperial) {
    const totalInches = Math.abs(diffInCm) / 2.54;
    const inches = Math.round(totalInches);
    const feet = Math.floor(inches / 12);
    const remainingInches = inches % 12;
    if (feet > 0) {
      return remainingInches > 0 ? `${feet}'${remainingInches}"` : `${feet}'`;
    }
    return `${inches}"`;
  }
  return `${Math.abs(diffInCm)} cm`;
}

export function getHeightRangeDescription(
  minHeight: number,
  maxHeight: number,
  userHeight: number | undefined,
  useImperial: boolean,
): string {
  if (!userHeight) {
    return `${formatHeight(minHeight, useImperial)} to ${formatHeight(maxHeight, useImperial)}`;
  }

  const minDiff = minHeight - userHeight;
  const maxDiff = maxHeight - userHeight;

  if (minDiff >= 0 && maxDiff > 0) {
    return `${formatHeightDiff(minDiff, useImperial)} to ${formatHeightDiff(maxDiff, useImperial)} taller than you (${formatHeight(userHeight, useImperial)})`;
  }

  if (minDiff < 0 && maxDiff <= 0) {
    const minShorter = Math.abs(maxDiff);
    const maxShorter = Math.abs(minDiff);
    return `${formatHeightDiff(minShorter, useImperial)} to ${formatHeightDiff(maxShorter, useImperial)} shorter than you (${formatHeight(userHeight, useImperial)})`;
  }

  const shorter = Math.abs(minDiff);
  const taller = maxDiff;
  return `${formatHeightDiff(shorter, useImperial)} shorter to ${formatHeightDiff(taller, useImperial)} taller than you (${formatHeight(userHeight, useImperial)})`;
}
